import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { fetchWeather, WeatherSnapshot, TempUnit, isUSLocation } from 'src/services/WeatherService';
import { fetchStockQuote, StockQuote } from 'src/services/StockQuoteService';
import { fetchFxRates, getFxCardConfig, FxCardConfig, FxRatesData } from 'src/services/FxRateService';
import { fetchNewsFeed, NewsFeedItem } from 'src/services/NewsFeedService';
import { fetchNflGames, NflGame } from 'src/services/NflScoresService';

/** Intervals for data refresh (ms) */
const STOCK_REFRESH_INTERVAL = 5 * 60 * 1000;
const WEATHER_REFRESH_INTERVAL = 15 * 60 * 1000;
const NFL_REFRESH_INTERVAL = 60 * 1000;
const NEWS_ROTATION_INTERVAL = 5000;

export interface WelcomeBannerData {
  weather: WeatherSnapshot | null;
  stock: StockQuote | null;
  fxConfig: FxCardConfig | null;
  fxIndex: number;
  setFxIndex: (index: number) => void;
  tempUnit: TempUnit;
  setTempUnit: (unit: TempUnit) => void;
  newsItems: NewsFeedItem[];
  newsIndex: number;
  newsHovered: boolean;
  setNewsHovered: (hovered: boolean) => void;
  nflGames: NflGame[];
}

/**
 * @feature landing-page
 * @description Orchestrates all data fetching, polling, and rotation logic for the WelcomeBanner.
 * Encapsulates weather, stock, FX, news feed, and NFL game state with auto-refresh intervals.
 */
export const useWelcomeBannerData = (): WelcomeBannerData => {
  const { Alias } = useAuth();

  const [weather, setWeather] = useState<WeatherSnapshot | null>(null);
  const [stock, setStock] = useState<StockQuote | null>(null);
  const [fxRatesData, setFxRatesData] = useState<FxRatesData | null>(null);
  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [fxConfig, setFxConfig] = useState<FxCardConfig | null>(null);
  const [fxIndex, setFxIndex] = useState(0);
  const [tempUnit, setTempUnit] = useState<TempUnit>(() => (localStorage.getItem('finsuite-temp-unit') as TempUnit) || (isUSLocation() ? 'F' : 'C'));
  const [newsIndex, setNewsIndex] = useState(0);
  const [newsHovered, setNewsHovered] = useState(false);
  const [newsItems, setNewsItems] = useState<NewsFeedItem[]>([]);
  const [nflGames, setNflGames] = useState<NflGame[]>([]);
  const [hireDate, setHireDate] = useState<string | undefined>(undefined);
  const hireDateRef = useRef<string | undefined>(undefined);

  // Initial data fetch + stock/NFL polling
  useEffect(() => {
    fetchStockQuote(hireDate).then(setStock);
    fetchFxRates().then((data) => {
      if (data) setFxRatesData(data);
    });
    fetchNewsFeed().then(setNewsItems);
    fetchNflGames().then(setNflGames);

    const stockInterval = setInterval(() => {
      fetchStockQuote(hireDateRef.current).then((q) => {
        if (q) setStock(q);
      });
    }, STOCK_REFRESH_INTERVAL);

    const nflInterval = setInterval(() => {
      fetchNflGames().then(setNflGames);
    }, NFL_REFRESH_INTERVAL);

    return () => {
      clearInterval(stockInterval);
      clearInterval(nflInterval);
    };
  }, []);

  // Keep hireDateRef in sync for interval closures
  useEffect(() => {
    hireDateRef.current = hireDate;
  }, [hireDate]);

  // Re-fetch stock with hire date once PAPI resolves it
  useEffect(() => {
    if (!hireDate) return;
    sessionStorage.removeItem('finsuite-stock-quote');
    fetchStockQuote(hireDate).then((q) => {
      if (q) setStock(q);
    });
  }, [hireDate]);

  // Restore saved FX currency selection when config loads
  useEffect(() => {
    if (!fxConfig) return;
    const saved = localStorage.getItem('finsuite-fx-currency');
    if (saved) {
      const idx = fxConfig.pairs.findIndex((p) => p.target === saved);
      if (idx >= 0) setFxIndex(idx);
    }
  }, [fxConfig]);

  // Resolve user country + hire date from PAPI
  useEffect(() => {
    if (!Alias) return;
    (async () => {
      try {
        const { getEmployeeByLogin } = await import('src/api/papi-service');
        const response = await getEmployeeByLogin(Alias, 'job');
        const country = response?.employee?.workLocation?.country;
        setUserCountry(country || 'USA');
        const lastHire = response?.employee?.job?.lastHireDate;
        if (lastHire) {
          setHireDate(lastHire.split('T')[0]);
        }
      } catch {
        setUserCountry('USA');
      }
    })();
  }, [Alias]);

  // Build FX config when rates + country are both available
  useEffect(() => {
    if (fxRatesData && userCountry) {
      setFxConfig(getFxCardConfig(fxRatesData, userCountry));
    }
  }, [fxRatesData, userCountry]);

  // Weather fetch + polling — re-runs when Alias resolves
  useEffect(() => {
    if (!Alias) return;
    fetchWeather({ userAlias: Alias }).then(setWeather);
    const weatherInterval = setInterval(() => {
      fetchWeather({ userAlias: Alias }).then((w) => {
        if (w) setWeather(w);
      });
    }, WEATHER_REFRESH_INTERVAL);
    return () => clearInterval(weatherInterval);
  }, [Alias]);

  // News ticker auto-rotation (pauses on hover)
  useEffect(() => {
    if (newsHovered || newsItems.length === 0) return;
    const tickerInterval = setInterval(() => {
      setNewsIndex((prev) => (prev + 1) % newsItems.length);
    }, NEWS_ROTATION_INTERVAL);
    return () => clearInterval(tickerInterval);
  }, [newsHovered, newsItems.length]);

  return {
    weather,
    stock,
    fxConfig,
    fxIndex,
    setFxIndex,
    tempUnit,
    setTempUnit,
    newsItems,
    newsIndex,
    newsHovered,
    setNewsHovered,
    nflGames
  };
};
